import { DIRECT_LINE_START_CONVERSATION_EVENT } from '../shared/directLine';
import {
  getConversationIdKey,
  setAuthenticatedConversationIdKey,
} from '../utils/sessionStorage';

const DIRECT_LINE_CONNECT_FULFILLED = 'DIRECT_LINE/CONNECT_FULFILLED';
const DIRECT_LINE_POST_ACTIVITY = 'DIRECT_LINE/POST_ACTIVITY';
const DIRECT_LINE_POST_ACTIVITY_FULFILLED =
  'DIRECT_LINE/POST_ACTIVITY_FULFILLED';
const DIRECT_LINE_POST_ACTIVITY_PENDING = 'DIRECT_LINE/POST_ACTIVITY_PENDING';
const DIRECT_LINE_POST_ACTIVITY_REJECTED = 'DIRECT_LINE/POST_ACTIVITY_REJECTED';
const EVENT = 'event';
const WEB_CHAT_SEND_MESSAGE = 'WEB_CHAT/SEND_MESSAGE';

export const createStartConversationActivity = ({
  code,
  currentConversationId,
  features,
  isMobile,
}) => {
  const value = {
    code,
    // Always sent, even when empty. The bot reads `features` unconditionally
    // rather than having to tell "no authenticated experiences are enabled"
    // apart from "this client is too old to send the key at all".
    features: Array.isArray(features) ? features : [],
    isMobile,
  };

  if (currentConversationId) {
    value.currentConversationId = currentConversationId;
  }

  return {
    meta: { method: 'keyboard' },
    payload: {
      activity: {
        channelData: { postBack: true },
        name: DIRECT_LINE_START_CONVERSATION_EVENT,
        type: EVENT,
        value,
      },
    },
    type: DIRECT_LINE_POST_ACTIVITY,
  };
};

export const createChatbotWebChatMiddleware =
  ({
    codeRef,
    featuresRef,
    freezeRef,
    hasConnectedRef,
    isMobile,
    isSessionPersistenceEnabled,
    // Starts that carry a code, keyed by Web Chat's id for the outgoing
    // activity. A start only counts once Direct Line accepts it, so a post that
    // fails leaves the conversation unmarked.
    pendingCodeStarts = new Set(),
  }) =>
  ({ dispatch }) =>
  next =>
  action => {
    if (
      freezeRef?.current &&
      (action.type === WEB_CHAT_SEND_MESSAGE ||
        action.type === DIRECT_LINE_POST_ACTIVITY)
    ) {
      return undefined;
    }

    if (action.type === DIRECT_LINE_CONNECT_FULFILLED) {
      if (hasConnectedRef) {
        // eslint-disable-next-line no-param-reassign
        hasConnectedRef.current = true;
      }
      dispatch(
        createStartConversationActivity({
          code: codeRef?.current,
          currentConversationId: isSessionPersistenceEnabled
            ? getConversationIdKey()
            : undefined,
          features: featuresRef?.current,
          isMobile,
        }),
      );
      // The code is single-use: vets-api destroys it on first redemption
      // attempt, so it must never be resent. CONNECT_FULFILLED can fire again
      // for the same session after a Direct Line reconnect, which would
      // otherwise resend this same (now-consumed) code and always fail.
      if (codeRef) {
        // eslint-disable-next-line no-param-reassign
        codeRef.current = undefined;
      }
    }

    if (action.type === DIRECT_LINE_POST_ACTIVITY_PENDING) {
      const activity = action.payload?.activity;
      if (
        activity?.name === DIRECT_LINE_START_CONVERSATION_EVENT &&
        activity.value?.code
      ) {
        pendingCodeStarts.add(action.meta?.clientActivityID);
      }
    }

    if (action.type === DIRECT_LINE_POST_ACTIVITY_REJECTED) {
      pendingCodeStarts.delete(action.meta?.clientActivityID);
    }

    if (
      action.type === DIRECT_LINE_POST_ACTIVITY_FULFILLED &&
      pendingCodeStarts.delete(action.meta?.clientActivityID) &&
      isSessionPersistenceEnabled
    ) {
      // We read the id when the post lands instead of when it was sent, so it's
      // the one the conversation actually has. A resumed page uses this to tell
      // a conversation the bot already got a code for from one that still needs
      // it.
      const conversationId = getConversationIdKey();
      if (conversationId) {
        setAuthenticatedConversationIdKey(conversationId);
      }
    }

    return next(action);
  };
